/**
 * WorkspaceUrlSync — keeps the primary pane's active tab and the URL in step.
 *
 * Restores the persisted active tab's URL when the app is entered at its
 * root, and captures URL changes onto the active primary tab's stored path.
 * Renders nothing: the primary pane has no tab strip. The sidebar is the
 * "where am I", and a rail click opens or focuses its place's one tab.
 */
import React, { useEffect, useRef } from 'react';

import { useLocation, useNavigate } from 'react-router-dom';

import { useInstalledApp } from '@shared/app-identity/useInstalledApp';

import { belongsToApp } from '../appScope';
import { isAppEntry } from '../entryState';
import { useWorkspaceStore } from '../stores/useWorkspaceStore';
import { titleForPath } from '../titleForPath';

export const WorkspaceUrlSync: React.FC = () => {
  const primaryActiveTabId = useWorkspaceStore(s => s.primaryActiveTabId);
  const tabs = useWorkspaceStore(s => s.tabs);
  const updateTab = useWorkspaceStore(s => s.updateTab);
  const installedApp = useInstalledApp();
  const location = useLocation();
  const navigate = useNavigate();
  const hydratedRef = useRef(false);

  // The tab stores the full client path — search included — so both effects
  // compare and record the same string.
  const fullPath = `${location.pathname}${location.search}`;

  // On first mount, navigate to the persisted active primary tab's path —
  // but only when the app was entered at its root. A deep link (bookmark,
  // shared URL, inbox digest link) lands on a real page and must win, or it
  // gets silently replaced by whatever tab was last open.
  useEffect(() => {
    if (hydratedRef.current) return;
    hydratedRef.current = true;
    if (!isAppEntry(location.state)) return;

    const activeTab = tabs.find(t => t.id === primaryActiveTabId);
    if (!activeTab || activeTab.path === fullPath) return;
    // Inside an installed app the persisted tab may belong to a different app
    // entirely — the store is shared with ordinary browser tabs. Staying on
    // this app's own home beats following it out of reach.
    if (!belongsToApp(activeTab.path, installedApp)) return;

    navigate(activeTab.path, { replace: true });
  }, [tabs, primaryActiveTabId, fullPath, location.state, navigate, installedApp]);

  // Capture in-page navigation onto the active primary tab, and re-title it
  // to match — same as the secondary pane's PaneNavigationProvider. Without
  // this a tab opened as "Financial" that navigates in-page to Budget still
  // reads "Financial", so open-or-focus identifies it as the Budget tab
  // while the row still says otherwise. The search string rides along: a
  // search-page tab that forgot its query on switch is a blank search page.
  // So does the navigation state, for the same reason.
  useEffect(() => {
    updateTab(primaryActiveTabId, {
      title: titleForPath(location.pathname),
      path: fullPath,
      // The real router's own state and key, recorded rather than re-derived.
      // A tab deactivated and come back to has to serve what it was reached
      // with, and a path alone is not that.
      state: location.state,
      locationKey: location.key,
    });
  }, [fullPath, location.pathname, location.state, location.key, primaryActiveTabId, updateTab]);

  return null;
};
